import { CalendarDays, ChevronDown } from 'lucide-react';
import { useField } from 'formik';
import { useMemo, useRef, useState } from 'react';

const FIELD =
  'w-full rounded bg-lend-field border text-base text-lend-ink placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-lend-blue/40 focus:border-lend-blue disabled:opacity-60';

const Label = ({ htmlFor, children, optional }: { htmlFor: string; children: string; optional?: boolean }) => (
  <label htmlFor={htmlFor} className="block text-sm md:text-base font-medium mb-2">
    {children}
    {optional && <span className="ml-1 font-normal text-lend-muted">(optional)</span>}
  </label>
);

const Error = ({ id, message }: { id: string; message?: string }) =>
  message ? (
    <p id={id} className="mt-1.5 text-sm text-error-600">
      {message}
    </p>
  ) : null;

interface FieldProps {
  name: string;
  label: string;
  placeholder?: string;
  optional?: boolean;
  disabled?: boolean;
}

export const TextField = ({ name, label, placeholder, optional, disabled, type = 'text', inputMode }: FieldProps & {
  type?: 'text' | 'email' | 'tel';
  inputMode?: 'text' | 'email' | 'tel' | 'numeric';
}) => {
  const [field, meta] = useField<string>(name);
  const error = meta.touched ? meta.error : undefined;
  return (
    <div>
      <Label htmlFor={name} optional={optional}>
        {label}
      </Label>
      <input
        {...field}
        value={field.value ?? ''}
        id={name}
        type={type}
        inputMode={inputMode}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={!!error}
        aria-describedby={error ? `${name}-error` : undefined}
        className={`${FIELD} h-12 md:h-[52px] px-4 ${error ? 'border-error-500' : 'border-lend-line'}`}
      />
      <Error id={`${name}-error`} message={error} />
    </div>
  );
};

export const DateField = ({ name, label, optional, disabled }: FieldProps) => {
  const [field, meta] = useField<string>(name);
  const error = meta.touched ? meta.error : undefined;
  return (
    <div>
      <Label htmlFor={name} optional={optional}>
        {label}
      </Label>
      <div className="relative">
        <CalendarDays className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-lend-blue" aria-hidden />
        <input
          {...field}
          value={field.value ?? ''}
          id={name}
          type="date"
          max={new Date().toISOString().slice(0, 10)}
          disabled={disabled}
          aria-invalid={!!error}
          aria-describedby={error ? `${name}-error` : undefined}
          className={`${FIELD} h-12 md:h-[52px] pl-12 pr-4 ${field.value ? '' : 'text-gray-400'} ${error ? 'border-error-500' : 'border-lend-line'}`}
        />
      </div>
      <Error id={`${name}-error`} message={error} />
    </div>
  );
};

export const TextArea = ({ name, label, placeholder, optional, disabled }: FieldProps) => {
  const [field, meta] = useField<string>(name);
  const error = meta.touched ? meta.error : undefined;
  return (
    <div>
      <Label htmlFor={name} optional={optional}>
        {label}
      </Label>
      <textarea
        {...field}
        value={field.value ?? ''}
        id={name}
        rows={3}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={!!error}
        aria-describedby={error ? `${name}-error` : undefined}
        className={`${FIELD} px-4 py-3 resize-none ${error ? 'border-error-500' : 'border-lend-line'}`}
      />
      <Error id={`${name}-error`} message={error} />
    </div>
  );
};

export interface Option {
  value: string;
  label: string;
}

/** A select the customer can type into to narrow a long list (the banks). */
export const SearchSelect = ({
  id,
  label,
  placeholder,
  options,
  value,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  placeholder: string;
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const selected = options.find((o) => o.value === value);
  const filtered = useMemo(
    () => options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase())),
    [options, query],
  );

  const choose = (option: Option) => {
    onChange(option.value);
    setQuery('');
    setOpen(false);
  };

  return (
    <div className="relative">
      <label htmlFor={id} className="block text-sm md:text-base font-medium mb-2">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          placeholder={selected ? selected.label : placeholder}
          value={open ? query : selected?.label ?? ''}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlight(0);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setHighlight((h) => Math.min(h + 1, filtered.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlight((h) => Math.max(h - 1, 0));
            } else if (e.key === 'Enter' && open && filtered[highlight]) {
              e.preventDefault();
              choose(filtered[highlight]);
            } else if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
          className={`${FIELD} h-12 md:h-[52px] pl-4 pr-10 border-lend-line ${selected && !open ? '' : ''}`}
        />
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-lend-muted" aria-hidden />
      </div>
      {open && (
        <ul
          id={`${id}-list`}
          ref={listRef}
          role="listbox"
          className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto rounded-md border border-lend-line bg-white shadow-lg"
        >
          {filtered.length === 0 && <li className="px-4 py-2.5 text-sm text-lend-muted">No bank matches “{query}”</li>}
          {filtered.map((option, index) => (
            <li
              key={option.value}
              role="option"
              aria-selected={option.value === value}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(option);
              }}
              onMouseEnter={() => setHighlight(index)}
              className={`px-4 py-2.5 text-sm cursor-pointer ${index === highlight ? 'bg-lend-blue-soft' : ''} ${
                option.value === value ? 'font-medium text-lend-blue-dark' : ''
              }`}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
